import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortInfo, IItemForGetShortDishInfo } from 'types';
import { Checkbox } from 'antd';
import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import SearchField from 'components/SearchField/SearchField';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import { ModificationDishCategorySelect } from './ModificationDishCategorySelect';
import { ModificationDishSubCategorySelect } from './ModificationDishSubCategorySelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './MultipleSelect.module.scss';

interface IProps {
	refetch: () => void;
	setFetchedModificationDishSubCategories: (value: IItemForGetShortInfo[]) => void;
	setModificationDishCategory: (value: Nullable<string>) => void;
	setModificationDishSubCategory: (value: Nullable<string>) => void;
	setModificationDishes: (value: string[]) => void;
	removeModificationDish: (value: string) => void;
	data: IItemForGetShortDishInfo[];
	IDsSelectedRestaurant: string[];
	selectedModificationDishCategory: Nullable<string>;
	selectedModificationDishSubCategory: Nullable<string>;
	fetchedModificationDishes: IItemForGetShortDishInfo[];
	selectedModificationDishes: string[];
	isLoading: boolean;
	responseError: boolean;
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({
		refetch,
		setFetchedModificationDishSubCategories,
		setModificationDishCategory,
		setModificationDishSubCategory,
		setModificationDishes,
		removeModificationDish,
		data,
		IDsSelectedRestaurant,
		selectedModificationDishCategory,
		selectedModificationDishSubCategory,
		fetchedModificationDishes,
		selectedModificationDishes,
		isLoading,
		responseError,
	}) => {
		const [searchString, setSearchString] = useState<string>('');

		const filteredDataWithModificationContent = useMemo(() => {
			const updatedData = data.map((el) => ({
				...el,
				additionalContent: (
					<>{el.externalId && <ChunkOfTextWithCopying text={el.externalId} className={style.externalId} />}</>
				),
			}));

			if (searchString) {
				const filteredDataByName = updatedData.filter((el) =>
					el.name.toLowerCase().includes(searchString.toLowerCase()),
				);
				const filteredDataByExternalId = updatedData.filter((el) =>
					el.externalId?.toLowerCase().includes(searchString.toLowerCase()),
				);
				const unionFilteredData = Array.from(new Set([...filteredDataByName, ...filteredDataByExternalId]));
				return unionFilteredData;
			}
			return updatedData;
		}, [searchString, data]);

		const handleSelectAllData = useCallback(() => {
			const isCheckedAllItems = data.every((el) => selectedModificationDishes.includes(el.id));
			const dataIDs = data.map((el) => el.id);
			if (isCheckedAllItems) {
				setModificationDishes(selectedModificationDishes.filter((el) => !dataIDs.includes(el)));
			} else {
				const filteredData = data.map((el) => el.id).filter((el) => !selectedModificationDishes.includes(el));
				setModificationDishes([...selectedModificationDishes, ...filteredData]);
			}
		}, [data, selectedModificationDishes, setModificationDishes]);

		useEffect(() => {
			setSearchString('');
		}, [selectedModificationDishSubCategory]);

		const selectedItemsList = useMemo(() => {
			return selectedModificationDishes.map((el) => {
				const label = fetchedModificationDishes?.find((item) => item.id === el)?.name || '';

				return <SelectedItem key={el} label={label} onDelete={() => removeModificationDish(el)} />;
			});
		}, [selectedModificationDishes, fetchedModificationDishes, removeModificationDish]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				const isChecked = () => {
					return data.every((el) => selectedModificationDishes.includes(el.id));
				};

				return (
					<div className={style.container}>
						<Text
							translationNameSpace="form"
							translationKey="fillInRequiredFilters"
							size="s"
							weight="bold"
						/>
						<div className={style.selectMenuHeader}>
							<div className={style.filterSelects}>
								<ModificationDishCategorySelect
									IDsSelectedRestaurant={IDsSelectedRestaurant}
									setModificationDishCategory={setModificationDishCategory}
								/>
								<ModificationDishSubCategorySelect
									selectedModificationDishCategory={selectedModificationDishCategory}
									setFetchedModificationDishSubCategories={setFetchedModificationDishSubCategories}
									setModificationDishSubCategory={setModificationDishSubCategory}
								/>
							</div>
							<SearchField onChangeSelectSearchString={setSearchString} searchString={searchString} />
							<Checkbox
								className={style.checkBox}
								checked={!!(data.length && isChecked())}
								disabled={!data.length}
								onChange={handleSelectAllData}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllDishesLabel"
									asFragment={true}
								/>
							</Checkbox>
							{!!selectedItemsList.length && (
								<div className={style.selectedItemsList}>{selectedItemsList}</div>
							)}
						</div>
						{menu}
					</div>
				);
			},
			[selectedModificationDishes, data, handleSelectAllData, setSearchString, searchString],
		);

		return (
			<FormSelect
				name="selectedModificationDishes"
				label="dishesLabel"
				options={filteredDataWithModificationContent}
				fetchError={responseError}
				loading={isLoading}
				disabled={!IDsSelectedRestaurant.length}
				onRefetch={refetch}
				onChange={setModificationDishes}
				counterValue={selectedModificationDishes.length}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
